import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/common";
import api from "@/lib/api";
import { Landmark, Bell, LogOut, Menu, ShieldCheck, Users, Newspaper, FlaskConical } from "lucide-react";
import { useEffect, useState } from "react";

const SPACE_SLUG = "campo-largo-2027";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.read).length)).catch(() => {});
  }, [user, loc.pathname]);

  if (!user) return null;

  const links = [
    { to: "/dashboard", label: t("Spaces"), key: "spaces", icon: Users },
    { to: "/news", label: t("News"), key: "news", icon: Newspaper },
    { to: "/science", label: t("Science"), key: "science", icon: FlaskConical },
    { to: "/representatives", label: t("Representatives"), key: "representatives", icon: Landmark },
  ];

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/dashboard" data-testid="nav-logo" className="font-display font-extrabold text-xl tracking-tight text-primary">Deliber<span className="text-secondary">.</span>Act</Link>
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.key}`} className={`px-3 py-2 text-sm font-medium rounded-sm transition-colors duration-200 ${loc.pathname.startsWith(l.to.split("/").slice(0,2).join("/")) ? "bg-primary text-white" : "text-muted-foreground hover:text-primary hover:bg-muted"}`}>{l.label}</Link>
          ))}
          {["admin", "moderator", "platform_owner"].includes(user.role) && (
            <Link to="/admin" data-testid="nav-admin" className={`px-3 py-2 text-sm font-medium rounded-sm transition-colors ${loc.pathname.startsWith("/admin") ? "bg-primary text-white" : "text-muted-foreground hover:text-primary hover:bg-muted"}`}>{t("Admin")}</Link>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/notifications" data-testid="nav-notifications" className="relative text-muted-foreground hover:text-primary">
            <Bell size={19} />
            {unread > 0 && <span className="absolute -top-1.5 -right-1.5 bg-destructive text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center" data-testid="notif-count">{unread}</span>}
          </Link>
          <Link to="/profile" data-testid="nav-profile" className="text-sm font-medium text-foreground hover:text-primary">{user.name}</Link>
          <button onClick={async () => { await logout(); nav("/"); }} data-testid="nav-logout" className="text-muted-foreground hover:text-destructive transition-colors"><LogOut size={18} /></button>
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle"><Menu /></button>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-white px-4 py-2 space-y-1">
          {links.map((l) => <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted"><l.icon size={16} /> {l.label}</Link>)}
          {["admin","moderator","platform_owner"].includes(user.role) && <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted"><ShieldCheck size={16}/> {t("Admin")}</Link>}
          <Link to="/notifications" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-muted"><Bell size={16}/> {t("Notifications")}</Link>
          <Link to="/profile" onClick={() => setOpen(false)} className="px-3 py-2 text-sm block hover:bg-muted rounded-sm">{user.name}</Link>
          <div className="px-3 py-2"><LanguageSwitcher /></div>
          <button onClick={async () => { await logout(); nav("/"); }} className="flex items-center gap-2 px-3 py-2 text-sm text-destructive w-full"><LogOut size={16}/> {t("Logout")}</button>
        </div>
      )}
    </header>
  );
}
